import { Badge, Checkbox, Group, Skeleton, Text, Tooltip, ActionIcon } from "@mantine/core";
import { IconDownload, IconEye } from "@tabler/icons-react";
import type { IVisitaCliente } from "../../../modelos/GestionArchivos/entidades/IVisitaCliente";
import type { INbsReporteDisponible } from "../../../modelos/NbsReportes/entidades/INbsReporteDisponible";
import { formatearFecha } from "../../Tareas/vista/constantesTablaTareas";

export const obtenerColumnas = (opts: {
    selectedIds: Record<string, boolean>;
    setSelectedIds: (v: Record<string, boolean> | ((prev: Record<string, boolean>) => Record<string, boolean>)) => void;
    consultarDescargarBase64Visita: (visita: IVisitaCliente) => void;
    previsualizarPDF: (visita: IVisitaCliente) => void;
    formatosNbsPorVisita: Record<number, INbsReporteDisponible>;
    tareasNbsValidadas: Record<number, boolean>;
    validarFormatosNbs: boolean;
}) => {
    const { selectedIds, setSelectedIds, consultarDescargarBase64Visita, previsualizarPDF, formatosNbsPorVisita, tareasNbsValidadas, validarFormatosNbs } = opts;

    // Nombre del archivo: el formato NBS si la visita es de Visita Instalación; mientras se valida, un esqueleto
    // para no mostrar primero el nombre del reporte de visita y cambiarlo después.
    const renderNombreArchivo = (visita: IVisitaCliente) => {
        const formatoNbs = formatosNbsPorVisita[visita.IdVisita];
        if (formatoNbs) {
            return (
                <Group gap={6} wrap="nowrap">
                    <Badge size="xs" variant="light" color="primary">NBS</Badge>
                    <Text size="sm">{formatoNbs.TipoVisitaNombre}</Text>
                </Group>
            );
        }
        if (validarFormatosNbs && !tareasNbsValidadas[visita.IdTarea]) {
            return <Skeleton height={12} width={140} radius="sm" />;
        }
        return <Text size="sm">{visita.NombreArchivo}</Text>;
    };

    return [
        {
            id: "select",
            header: ({ table }: any) => {
                const rows = table.getRowModel().rows || [];
                const allSelected = rows.length > 0 && rows.every((r: any) => !!selectedIds[r.original.IdVisita]);
                const someSelected = rows.some((r: any) => !!selectedIds[r.original.IdVisita]);

                return (
                    <Checkbox
                        size="xs"
                        checked={allSelected}
                        style={{ cursor: 'pointer' }}
                        indeterminate={someSelected && !allSelected}
                        onChange={(e) => {
                            const checked = e.currentTarget.checked;
                            if (checked) {
                                const nuevos: Record<string, boolean> = {};
                                rows.forEach((r: any) => {
                                    nuevos[r.original.IdVisita] = true;
                                });
                                setSelectedIds((prev) => ({ ...prev, ...nuevos }));
                                return;
                            }

                            // if unchecking, remove visible rows from selection to keep object clean
                            setSelectedIds((prev) => {
                                const copia: Record<string, boolean> = { ...prev };
                                rows.forEach((r: any) => {
                                    delete copia[r.original.IdVisita];
                                });
                                return copia;
                            });
                        }}
                    />
                );
            },
            cell: ({ row }: any) => (
                <Checkbox
                    size="xs"
                    style={{ cursor: 'pointer' }}
                    checked={!!selectedIds[row.original.IdVisita]}
                    onChange={(e) => {
                        const checked = e.currentTarget.checked;
                        const id = row.original.IdVisita;
                        setSelectedIds((prev) => {
                            const copia: Record<string, boolean> = { ...prev };
                            if (checked) {
                                copia[id] = true;
                            } else {
                                delete copia[id];
                            }
                            return copia;
                        });
                    }}
                />
            ),
            enableSorting: false,
            enableHiding: false,
            accessorKey: "IdVisita",

        },
        {
            header: "Código Tarea",
            accessorKey: "IdTarea",
        },
        {
            header: "Proyecto",
            accessorKey: "NombreProyecto",
        },
        {
            header: "Código Equipo",
            accessorKey: "CodigoEquipo",
        },
        {
            header: "Fecha Inicio Visita",
            accessorKey: "FechaInicioVisita",
            cell: ({ row }: any) => `${formatearFecha(row.original.FechaInicioVisita)}`
        },
        {
            header: "Fecha Fin Visita",
            accessorKey: "FechaFinVisita",
            cell: ({ row }: any) => `${formatearFecha(row.original.FechaFinVisita)}`
        },
        {
            header: "Archivo",
            accessorKey: "NombreArchivo",
            cell: ({ row }: any) => (
                <Group justify="center" gap={8}>
                    {renderNombreArchivo(row.original)}
                    <Tooltip label="Previsualizar" style={{ fontSize: 10 }} withArrow>
                        <ActionIcon
                            variant="light"
                            color="primary"
                            size="sm"
                            onClick={() => previsualizarPDF(row.original)}
                        >
                            <IconEye size={14} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Descargar" style={{ fontSize: 10 }} withArrow>
                        <ActionIcon
                            variant="light"
                            size="sm"
                            onClick={() => consultarDescargarBase64Visita(row.original)}
                        >
                            <IconDownload size={14} />
                        </ActionIcon>
                    </Tooltip>
                </Group>
            ),
        },
    ];
};

export default obtenerColumnas;
